import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import SnackbarUtils from "utils/SnackbarUtils";
import { accountCodeService, wmsItemService } from "services";
import type { Unit, WmsItem, WmsItemGroup } from "types";
import { useWmsItemGroup } from "hooks";

type PersistMeta = {
  pinned?: boolean;
  onRevert?: () => void;
  onSuccess?: () => void;
  refetch?: boolean;
};

const parseNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseBoolean = (value: unknown, defaultValue = false): boolean => {
  if (value === true || value === "true" || value === 1 || value === "1")
    return true;
  if (value === false || value === "false" || value === 0 || value === "0")
    return false;
  return defaultValue;
};

const WmsItems: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<WmsItem | null>(null);
  const originalRowDataRef = useRef<Map<string | number, WmsItem>>(new Map());
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [rows, setRows] = useState<WmsItem[]>([]);
  const [itemGroups, setItemGroups] = useState<WmsItemGroup[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [confirmRow, setConfirmRow] = useState<WmsItem | null>(null);
  const [showInlineAddRow, setShowInlineAddRow] = useState<
    "add" | "edit" | undefined
  >("add");
  const [queryParams, setQueryParams] = useState<{
    page: number;
    pageSize: number;
  }>({
    page: 1,
    pageSize: 20,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await wmsItemService.list();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      SnackbarUtils.error("Tải danh sách hàng hóa thất bại");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { fetchList: fetchWmsItemGroups } = useWmsItemGroup();
  const { data: wmsItemGroups } = fetchWmsItemGroups(true);

  useEffect(() => {
    setItemGroups(Array.isArray(wmsItemGroups) ? wmsItemGroups : []);
  }, [wmsItemGroups]);

  useEffect(() => {
    accountCodeService
      .list({ page: 1, pageSize: 1000, type: "unit" })
      .then((res) => {
        const list =
          res?.data && Array.isArray(res.data) ? (res.data as Unit[]) : [];
        setUnits(list);
      })
      .catch(() => setUnits([]));
  }, []);

  const columns = useMemo<ColDef<WmsItem>[]>(() => {
    const groupIdOptions = itemGroups.map((g) => String(g.id));
    const groupDisplay = new Map(
      itemGroups.map((g) => [
        String(g.id),
        `${g.code}${g.name ? ` - ${g.name}` : ""}`,
      ]),
    );
    const unitOptions = units.map((u) => String(u.id));
    const unitDisplay = new Map(
      units.map((u) => [
        String(u.id),
        `${u.code}${u.name ? ` - ${u.name}` : ""}`,
      ]),
    );

    return [
      {
        headerName: "Mã hàng",
        field: "code",
        minWidth: 140,
      },
      {
        headerName: "Tên hàng",
        field: "name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Nhóm hàng",
        field: "group_id",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: groupIdOptions },
        valueFormatter: (params) =>
          groupDisplay.get(String(params.value)) ?? params.value ?? "",
        valueParser: (params) => parseNumber(params.newValue),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Đơn vị tính",
        field: "unit_id",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: unitOptions },
        valueFormatter: (params) =>
          unitDisplay.get(String(params.value)) ?? params.value ?? "",
        valueParser: (params) => parseNumber(params.newValue),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Xuất xứ",
        field: "origin",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Giá bán",
        field: "price",
        minWidth: 140,
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
        valueParser: (params) => parseNumber(params.newValue),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Bảo hành (tháng)",
        field: "warranty_months",
        minWidth: 160,
        valueParser: (params) => parseNumber(params.newValue),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Chiết khấu (%)",
        field: "discount_percent",
        minWidth: 140,
        valueParser: (params) => parseNumber(params.newValue),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "VAT reduction ID",
        field: "vat_reduction_id",
        minWidth: 160,
        valueParser: (params) => parseNumber(params.newValue),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Barcode",
        field: "barcode",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Ngưng hoạt động",
        field: "is_inactive",
        minWidth: 160,
        cellEditor: "agCheckboxCellEditor",
        valueFormatter: (params) =>
          parseBoolean(params.value) ? "Ngưng" : "Hoạt động",
        filter: "agSetColumnFilter",
        filterParams: { values: [true, false] },
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 220,
        flex: 1,
        filter: "agTextColumnFilter",
      },
    ];
  }, [itemGroups, units]);

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      code: "",
      name: "",
      group_id: undefined,
      unit_id: undefined,
      origin: "",
      price: undefined,
      warranty_months: undefined,
      description: "",
      discount_percent: undefined,
      vat_reduction_id: undefined,
      barcode: "",
      is_inactive: false,
    };
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    async (row: Partial<WmsItem>, meta?: PersistMeta) => {
      const payload: WmsItem = {
        code: String(row.code ?? "").trim(),
        name: String(row.name ?? "").trim(),
        group_id: parseNumber(row.group_id),
        unit_id: parseNumber(row.unit_id),
        origin: row.origin ? String(row.origin).trim() : undefined,
        price: parseNumber(row.price),
        warranty_months: parseNumber(row.warranty_months),
        description: row.description
          ? String(row.description).trim()
          : undefined,
        discount_percent: parseNumber(row.discount_percent),
        vat_reduction_id: parseNumber(row.vat_reduction_id),
        barcode: row.barcode ? String(row.barcode).trim() : undefined,
        is_inactive: parseBoolean(row.is_inactive, false),
      };

      if (!payload.code || !payload.name) {
        SnackbarUtils.error("Mã hàng và Tên hàng là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      setLoading(true);
      try {
        const shouldCreate = !row.id || meta?.pinned;

        // Store original data before update
        if (!shouldCreate && row.id) {
          const currentRow = rows.find((r) => r.id === row.id);
          if (currentRow) {
            originalRowDataRef.current.set(row.id, { ...currentRow });
          }
        }

        if (shouldCreate) {
          const res = await wmsItemService.create(payload);
          if (res?.data) {
            setRows((prev) => [res.data as WmsItem, ...prev]);
            SnackbarUtils.success(res?.message ?? "Thêm hàng hóa thành công");
          } else {
            SnackbarUtils.error(res?.message ?? "Thêm hàng hóa thất bại");
            meta?.onRevert?.();
            return;
          }
        } else {
          const res = await wmsItemService.update(row.id, payload);
          if (res?.data) {
            setRows((prev) =>
              prev.map((item) =>
                item.id === row.id ? (res.data as WmsItem) : item,
              ),
            );
            SnackbarUtils.success(
              res?.message ?? "Cập nhật hàng hóa thành công",
            );
          } else {
            SnackbarUtils.error(res?.message ?? "Cập nhật hàng hóa thất bại");

            // Revert UI state to original data
            if (row.id && originalRowDataRef.current.has(row.id)) {
              const original = originalRowDataRef.current.get(row.id)!;
              setRows((prev) =>
                prev.map((item) => (item.id === row.id ? original : item)),
              );
              originalRowDataRef.current.delete(row.id);
            }

            meta?.onRevert?.();
            return;
          }
        }
        if (meta?.refetch) fetchData();
        meta?.onSuccess?.();
      } catch (error) {
        SnackbarUtils.error("Lưu hàng hóa thất bại");
        meta?.onRevert?.();
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  const handleSaveRow = useCallback(
    (row: WmsItem, meta?: { pinned?: boolean }) => {
      // CRITICAL: Stop editing to commit cell values
      if (gridApiRef.current) {
        gridApiRef.current.stopEditing();
      }

      // Small delay to ensure commit completes
      setTimeout(() => {
        persistRow(row, {
          pinned: meta?.pinned,
          refetch: meta?.pinned || !row.id,
          onSuccess: () => {
            resetInlineAddRow();
            gridApiRef.current?.stopEditing();
            setShowInlineAddRow("add");
          },
        });
      }, 50);
    },
    [persistRow, resetInlineAddRow],
  );

  const handleEditRow = useCallback((params) => {
    setShowInlineAddRow(undefined);
    setTimeout(() => {
      gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "code",
      });
    }, 0);
    // highlight editing row in action buttons
    setTimeout(() => {
      gridApiRef.current?.refreshCells({ columns: ["__actions"], force: true });
    }, 0);
  }, []);

  const handleDeleteRow = useCallback((row: WmsItem) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmRow?.id) return;
    setLoading(true);
    try {
      await wmsItemService.destroy(confirmRow.id);
      setRows((prev) => prev.filter((item) => item.id !== confirmRow.id));
      setConfirmRow(null);
    } catch (error) {
      SnackbarUtils.error("Xóa hàng hóa thất bại");
    } finally {
      setLoading(false);
    }
  }, [confirmRow]);

  const handleQueryChange = useCallback((query: QueryState) => {
    setQueryParams((prev) => {
      const next = {
        ...prev,
        page: query.pagination?.page ?? prev.page,
        pageSize: query.pagination?.pageSize ?? prev.pageSize,
      };
      const unchanged =
        next.page === prev.page && next.pageSize === prev.pageSize;
      return unchanged ? prev : next;
    });
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () =>
      showInlineAddRow && addRowRef.current
        ? { ...addRowRef.current }
        : undefined,
    [addRowVersion, showInlineAddRow],
  );

  const handleAfterActions = useCallback(() => {
    resetInlineAddRow();
    setShowInlineAddRow("add");
    gridApiRef.current?.stopEditing();
  }, [resetInlineAddRow]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Hàng hóa (WMS)</h1>
        <p className="text-sm text-gray-600">
          Danh sách hàng hóa, cho phép thêm, sửa, xóa trực tiếp trên bảng.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách hàng hóa
            </h2>
            <p className="text-xs text-gray-500">
              Chỉnh sửa nhanh, lưu trực tiếp.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const next = showInlineAddRow ? undefined : "add";
              if (next) {
                resetInlineAddRow();
              } else {
                addRowRef.current = null;
                setAddRowVersion((v) => v + 1);
                gridApiRef.current?.stopEditing();
              }
              setShowInlineAddRow(next);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {showInlineAddRow ? "Ẩn dòng thêm" : "Thêm nhanh"}
          </button>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={Math.ceil(rows.length / queryParams.pageSize)}
          onSave={handleSaveRow}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          getRowId={(params) =>
            String(params.data.id ?? `new-${params.rowIndex}`)
          }
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
          onAfterActions={handleAfterActions}
        />

        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang tải hoặc lưu dữ liệu hàng hóa."
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa hàng hóa?"
        description={`Bạn chắc chắn muốn xóa hàng hóa "${confirmRow?.code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default WmsItems;
